import mapper from '../../../libraries/utils/auto-mapper';
import { camelCaseToUnderscore } from 'utils/camel-case-and-const-name';
import ErrorNotifier from 'app/scripts/services/error-notifier';
import { getSpotConfig, setSpotConfig, setV1Config } from '@host-panel/request/config';
import { dispatch } from 'state/store';
import { updateSpot } from '../../../spots/spotsSlice';
import { mapToSpotProps } from '../../../spots/spotsUtils';

const TENANT_CONFIGS_SPOT_POLICIES_KEYS_MAP = {
  default_policy: 'default',
  comments_containing_images: 'image',
  comments_containing_external_links: 'external_link',
  comments_containing_gifs: 'animation'
};

const TENANT_CONFIGS_KEYS_MAP = {
  ...TENANT_CONFIGS_SPOT_POLICIES_KEYS_MAP,
  block_report_by_registered: 'block_report_by_registered'
};

const isPropertyInSpotPolicies = propName => Object.values(TENANT_CONFIGS_SPOT_POLICIES_KEYS_MAP).includes(propName);

const TENANT_MODERATION_KEY = 'tenant.moderation';
const TENANT_MODERATION_SPOT_POLICIES_KEY = `${TENANT_MODERATION_KEY}.spot_policies`;

export default async function editSettings(context, payloadWithSendEvent, done) {
  if (!payloadWithSendEvent || typeof payloadWithSendEvent !== 'object') {
    throw new Error('editSettings in app/scripts/actions/spot/edit-settings.js threw an error!');
  }

  const { sendEvent, ...payload } = payloadWithSendEvent;

  var spotId = payload.spotId;

  let v2RedisConfigRequest = [];
  let tenantRequestPayload = [];
  for (var field in payload) {
    if (payload.hasOwnProperty(field) && field !== 'spotId') {
      const param = camelCaseToUnderscore(field);
      const config = { key: param, value: payload[field] };
      if (TENANT_CONFIGS_KEYS_MAP[param]) {
        config.key = TENANT_CONFIGS_KEYS_MAP[param];
        tenantRequestPayload.push(config);
        continue;
      }
      v2RedisConfigRequest.push(config);
    }
  }

  let be_success = true;

  try {
    let finalRes = {};
    if (tenantRequestPayload.length > 0) {
      const [firstPropertyInList] = tenantRequestPayload;
      const isSpotPoliciesProp = isPropertyInSpotPolicies(firstPropertyInList?.key);
      const tenantRes = isSpotPoliciesProp
        ? await setTenantConfigForSpotPolicies(spotId, tenantRequestPayload)
        : await setTenantConfigForModerationProperty(spotId, firstPropertyInList);
      finalRes = { ...finalRes, ...tenantRes };
    }
    if (v2RedisConfigRequest.length > 0) {
      const v1Res = await setV1Config(spotId, v2RedisConfigRequest);
      finalRes = { ...finalRes, ...v1Res };
    }

    const data = { id: spotId, ...finalRes };

    dispatch(updateSpot(mapToSpotProps(data)));
  } catch (reason) {
    be_success = false;

    console.error(reason);
    ErrorNotifier.notifyError(`Failed to edit spot settings (edit-settings.js)`, {
      settings: finalRes,
      error: reason.message
    });
  } finally {
    sendEvent?.(be_success);
    done();
  }
}

const getTenantConfigResponseValueFromData = data => data[0]?.value;

async function setTenantConfigForModerationProperty(spotId, moderationProp = {}) {
  const { key, value } = moderationProp;
  const { data } = await setSpotConfig(spotId, [{ key: `${TENANT_MODERATION_KEY}.${key}`, value }]);

  return { [key]: getTenantConfigResponseValueFromData(data) };
}

async function setTenantConfigForSpotPolicies(spotId, requestPayload) {
  const currentConfig = await getSpotConfig(spotId, [TENANT_MODERATION_SPOT_POLICIES_KEY]);
  let spotPolicies = currentConfig?.tenant?.moderation?.spot_policies?.value;
  requestPayload.forEach(field => {
    spotPolicies[field?.key] = field.value;
  });
  const { data } = await setSpotConfig(spotId, [{ key: TENANT_MODERATION_SPOT_POLICIES_KEY, value: spotPolicies }]);

  return getTenantConfigResponseValueFromData(data);
}

export function parse(data) {
  return mapper.map(data, {
    external_link: 'spot.commentsContainingExternalLinks',
    image: 'spot.commentsContainingImages',
    animation: 'spot.commentsContainingGifs',
    default: 'spot.defaultPolicy',
    allow_guests_to_like: 'spot.allowGuestsToLike',
    force_register: 'spot.forceRegister',
    roger_mode: 'spot.isRogerMode',
    multiple_moderators_mode: 'spot.isMultipleModeratorsMode',
    user_moderation_enabled: 'spot.isRestrictedUNsEnabled',
    block_report_by_registered: 'spot.blockReportByRegistered'
  });
}
